import React from "react";
import { Unlock, CheckCircle2, AlertCircle, Compass, Loader2, Footprints } from "lucide-react";

/**
 * DistanceTile — allineato al mockup PDF Odismap:
 * - Sfondo verde forest
 * - Illustrazione camminatore a sinistra
 * - Numero grande "X metri" al centro
 * - Sub descrittivo dello stato
 * - Barra di avvicinamento arancione (sotto)
 *
 * Diventa cliccabile "Sblocca" quando onSpot=true.
 */
export default function DistanceTile({ distance, trend, onSpot, busy = false, onUnlock, onMissing }) {
  // Stato + copy
  let label, sub, IconBig, animateIcon = "";
  const clickable = onSpot && !busy;

  if (onSpot) {
    label = "Sei sul posto!";
    sub = "Tocca per sbloccare";
    IconBig = Unlock;
    animateIcon = "animate-pulse";
  } else if (distance === null || distance === undefined) {
    label = "Attendo GPS…";
    sub = "Esci all'aperto per un segnale migliore";
    IconBig = Loader2;
    animateIcon = "animate-spin";
  } else if (distance < 15) {
    label = `${Math.round(distance)} metri`;
    sub = "Quasi arrivato — pochi passi ancora!";
    IconBig = CheckCircle2;
  } else if (trend === "cold") {
    label = `${Math.round(distance)} metri`;
    sub = "Acqua… ti stai allontanando";
    IconBig = AlertCircle;
  } else if (distance > 300) {
    label = `${Math.round(distance)} metri`;
    sub = "Sei ancora lontano dall'obiettivo";
    IconBig = Compass;
  } else if (trend === "hot" || (trend === "warm" && distance < 40)) {
    label = `${Math.round(distance)} metri`;
    sub = "Fuochino! Continua, ci sei quasi";
    IconBig = Footprints;
  } else if (trend === "warm" || trend === "approaching") {
    label = `${Math.round(distance)} metri`;
    sub = "Ti stai avvicinando…";
    IconBig = Footprints;
  } else {
    label = `${Math.round(distance)} metri`;
    sub = "Cammina per capire la direzione";
    IconBig = Footprints;
  }

  // Progress 0..1 — quanto sono vicino (>200m = 0, <15m = 1)
  let pct = 0;
  if (onSpot) pct = 1;
  else if (typeof distance === "number") {
    const cap = 200;
    pct = Math.min(1, Math.max(0, 1 - Math.min(distance, cap) / cap));
  }

  return (
    <div data-testid="distance-tile-wrapper" className="space-y-2">
      <button
        type="button"
        onClick={() => {
          if (clickable) onUnlock?.();
          else if (onMissing) onMissing();
        }}
        disabled={busy}
        aria-disabled={!clickable}
        className={`w-full rounded-3xl bg-forest text-white px-5 py-5 sm:py-6 text-left transition-transform shadow-tactile ${
          clickable ? "cursor-pointer hover:scale-[1.01] active:scale-[0.99] ring-4 ring-gold/40" : "cursor-default"
        }`}
        data-testid="distance-tile"
        data-onspot={onSpot ? "true" : "false"}
        data-clickable={clickable ? "true" : "false"}
      >
        <div className="flex items-center gap-4">
          {/* Camminatore illustrazione */}
          <div className="shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-forest-hover/70 flex items-center justify-center">
            <IconBig size={40} className={`text-gold ${animateIcon}`} strokeWidth={2.2}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-display font-extrabold text-white leading-none ${onSpot ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl"}`} data-testid="distance-tile-label">
              {label}
            </div>
            <div className="mt-1.5 text-sm sm:text-base font-bold text-white/85 leading-snug" data-testid="distance-tile-sub">
              {sub}
            </div>
          </div>
          {onSpot && (
            <div className="shrink-0 hidden sm:block">
              <div className="px-4 py-2 rounded-full bg-gold text-white font-display font-extrabold animate-bounce-in">
                SBLOCCA →
              </div>
            </div>
          )}
        </div>
        {onSpot && (
          <div className="mt-3 sm:hidden text-center">
            <div className="inline-block px-4 py-2 rounded-full bg-gold text-white font-display font-extrabold animate-bounce-in">
              TOCCA PER SBLOCCARE →
            </div>
          </div>
        )}
      </button>

      {/* Barra progresso arancione (avvicinamento) */}
      <div className="w-full h-3 rounded-full bg-outline overflow-hidden" data-testid="distance-progress">
        <div
          className="h-full bg-gold transition-all duration-700 ease-out"
          style={{ width: `${Math.round(pct * 100)}%` }}
        />
      </div>
    </div>
  );
}
